'use client';

import * as React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowLeft } from 'lucide-react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getPreviousPathname } from '@/lib/navigation-history';

const matchesExcluded = (pathname: string, excludeReferrers: string[]): boolean =>
  excludeReferrers.some((path) => pathname === path || pathname.startsWith(`${path}/`));

const hasSafeInternalHistory = (excludeReferrers?: string[]): boolean => {
  if (typeof window === 'undefined') return false;
  if (window.history.length <= 1) return false;

  // Prefer the in-app pathname tracker — Next.js' App Router uses SPA
  // navigation, so document.referrer is frozen at the document's initial
  // load and stops reflecting reality after the first hop. The tracker is
  // updated by LayoutRenderer's pathname effect.
  const inAppPrev = getPreviousPathname();
  if (inAppPrev) {
    if (excludeReferrers && matchesExcluded(inAppPrev, excludeReferrers)) {
      return false;
    }
    return true;
  }

  // No in-app history yet (direct-load on this session). Fall back to the
  // browser's referrer, which catches the case where another site or a
  // bookmark sent the user straight into this page.
  const referrer = document.referrer;
  if (!referrer) return false;
  try {
    const url = new URL(referrer);
    if (url.origin !== window.location.origin) return false;
    if (excludeReferrers && matchesExcluded(url.pathname, excludeReferrers)) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

/**
 * Returns a function that navigates back when the previous in-app page is
 * safe to return to, falling back to `fallbackHref` on direct loads, external
 * referrers, empty history, or referrers matching any of `excludeReferrers`.
 * Use inside dialog confirm handlers so that "Discard & go back" gets the
 * same safe-fallback behavior as the button.
 */
export function useBackNavigation(
  fallbackHref: string = '/',
  excludeReferrers?: string[],
) {
  const router = useRouter();
  const excludeKey = excludeReferrers?.join('|') ?? '';
  return React.useCallback(() => {
    if (hasSafeInternalHistory(excludeReferrers)) {
      router.back();
    } else {
      router.push(fallbackHref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, fallbackHref, excludeKey]);
}

export interface BackButtonProps
  extends Omit<ButtonProps, 'asChild' | 'children'> {
  /** Hard destination. Renders as a Link and ignores history entirely. */
  href?: string;
  /** Destination used when there's no safe in-app history to return to. */
  fallbackHref?: string;
  /** Visible label. */
  label?: string;
  /**
   * Referrer pathname prefixes that should be treated as unsafe — when the
   * previous page matches one of these, `fallbackHref` is used instead of
   * `router.back()`. Example: `['/checkout']` on the cart prevents the
   * back button from re-entering an in-progress checkout.
   */
  excludeReferrers?: string[];
}

export const BackButton = React.forwardRef<HTMLButtonElement, BackButtonProps>(
  function BackButton(
    {
      href,
      fallbackHref = '/',
      label = 'Back',
      variant = 'outline',
      size = 'sm',
      className,
      onClick,
      excludeReferrers,
      ...props
    },
    ref,
  ) {
    const navigateBack = useBackNavigation(fallbackHref, excludeReferrers);

    if (href) {
      return (
        <Button
          asChild
          variant={variant}
          size={size}
          className={cn('gap-2', className)}
        >
          <Link href={href}>
            <ArrowLeft className="h-4 w-4" aria-hidden />
            {label}
          </Link>
        </Button>
      );
    }

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn('gap-2', className)}
        onClick={onClick ?? (() => navigateBack())}
        {...props}
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {label}
      </Button>
    );
  },
);
